import { Controller } from "@hotwired/stimulus"
import Choices from "choices.js"

// Progressively enhances UI::SelectComponent's native <select> with
// choices.js.
//
// data-controller lives on a wrapper div, not the <select> itself: choices.js
// reparents the select into its own generated wrapper on init, and if the
// controller were attached to the select, Stimulus's MutationObserver would
// see it get removed/re-added and reconnect the controller — which would
// reinitialize choices.js and reparent it again, looping forever and hanging
// the tab.
export default class extends Controller {
  static targets = ["select"]
  static values = { renderIcons: Boolean }

  connect() {
    // choices.js only mirrors the page's direction (e.g. flipping the
    // dropdown caret to the left in RTL) when the select's own `dir` differs
    // from the document's computed direction — it never just inherits it.
    // Since this app's document is always RTL, that mismatch never happens
    // on its own, so it's set explicitly here.
    this.selectTarget.dir = document.documentElement.dir

    this.choices = new Choices(this.selectTarget, {
      searchEnabled: this.selectTarget.options.length > 8,
      itemSelectText: "",
      shouldSort: false,
      // Defaults are renderChoiceLimit: -1 (render every choice's DOM node
      // up front) and searchResultLimit: 4 — fine for a dozen options, but
      // the icon picker backs this with 5000+ names. Cap both so opening it
      // doesn't build thousands of DOM nodes and typing still surfaces a
      // useful number of matches.
      renderChoiceLimit: 50,
      searchResultLimit: 50,
    })

    if (this.renderIconsValue) this.observeIconChoices()
  }

  disconnect() {
    this.iconObserver?.disconnect()
    this.choices?.destroy()
  }

  // choices.js reads each choice's label via the native <option>.label DOM
  // property (see WrappedSelect#_optionToChoice in choices.js), which is
  // always plain text — a browser flattens any markup written inside an
  // <option> down to its text content, so there's no way to get an <i>
  // glyph into the label through the option itself, HTML-escaping settings
  // aside. Decorating the rendered choice/item nodes directly, keyed off
  // their `data-value` (the icon name), sidesteps that limitation instead
  // of reimplementing choices.js's ~40-line internal template function.
  observeIconChoices() {
    this.iconObserver = new MutationObserver(() => this.decorateIconItems())
    this.iconObserver.observe(this.element, { childList: true, subtree: true })
    this.decorateIconItems()
  }

  decorateIconItems() {
    this.element.querySelectorAll(".choices__item[data-value]:not([data-icon-decorated])").forEach((el) => {
      const name = el.dataset.value
      el.dataset.iconDecorated = "true"
      if (!name) return

      const icon = document.createElement("i")
      icon.className = `ti ti-${name}`
      el.prepend(icon, " ")
    })
  }
}
